import React from 'react';
import { Button } from '@/components/ui/button';
import type { EffectiveReportAreaScope } from './reportAreaScope';
import { useAorText } from 'utils/aorTerminology';

type ReportAreaScopeBarProps = {
  defaultScope: EffectiveReportAreaScope | null;
  customScope: unknown;
  isEditingArea: boolean;
  /** Incident reports edit and reset the incident location, not the linked AOR. */
  areaKind?: 'incident' | 'aor';
  disabled?: boolean;
  /** Rendered before the area controls (e.g. Draft status). */
  leading?: React.ReactNode;
  /** Rendered after the status badge (e.g. Edit before Approval). */
  trailing?: React.ReactNode;
  onEditOnMap: () => void;
  onCancelDraw: () => void;
  onResetScope: () => void;
};

const ReportAreaScopeBar: React.FC<ReportAreaScopeBarProps> = ({
  defaultScope,
  customScope,
  isEditingArea,
  areaKind = 'aor',
  disabled = false,
  leading,
  trailing,
  onEditOnMap,
  onCancelDraw,
  onResetScope,
}) => {
  const aorText = useAorText();
  if (disabled && !leading && !trailing) return null;

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      {leading}
      {trailing}
      {!disabled ? (
        <>
          {isEditingArea ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive tw:active:bg-destructive/10 tw:active:text-destructive"
              title={
                areaKind === 'incident'
                  ? 'Cancel incident location edit'
                  : aorText('Cancel AOR edit')
              }
              onClick={onCancelDraw}
            >
              {areaKind === 'incident'
                ? 'Cancel Incident Location Edit'
                : aorText('Cancel AOR Edit')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEditOnMap}
            >
              {areaKind === 'incident'
                ? 'Edit Incident Location'
                : aorText('Edit AOR on Map')}
            </Button>
          )}
          {customScope ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!defaultScope}
              onClick={onResetScope}
            >
              {areaKind === 'incident'
                ? 'Reset to Incident'
                : aorText('Reset to AOR')}
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ReportAreaScopeBar;
